import { useState, useEffect } from 'preact/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Code, 
  FileText, 
  GitBranch, 
  Package, 
  Wrench, 
  Database, 
  TestTube,
  Layers,
  BarChart3,
  TrendingUp,
  Clock,
  Users,
  Star,
  Download,
  Activity,
  Zap,
  Shield,
  Globe,
  Smartphone,
  Monitor,
  Server,
  RefreshCw
} from 'lucide-preact';
import { CodeMetrics, CodeAnalyzer } from '../services/codeAnalysis';

interface ProjectOverviewProps {
  projectId: string;
  projectName: string;
}

export function ProjectOverview({ projectId, projectName }: ProjectOverviewProps) {
  const [metrics, setMetrics] = useState<CodeMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjectAnalysis();
  }, [projectId]);

  const loadProjectAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/projects/${projectId}/analysis`);
      if (!response.ok) {
        throw new Error(`Failed to fetch analysis: ${response.statusText}`);
      }
      
      const analysis = await response.json();
      setMetrics(analysis);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze project');
    } finally {
      setLoading(false);
    }
  };

  const getFrameworkIcon = (type: string) => {
    switch (type) {
      case 'frontend': return <Globe className="h-4 w-4" />;
      case 'backend': return <Server className="h-4 w-4" />;
      case 'database': return <Database className="h-4 w-4" />;
      case 'testing': return <TestTube className="h-4 w-4" />;
      case 'build': return <Wrench className="h-4 w-4" />;
      default: return <Package className="h-4 w-4" />;
    }
  };

  const getToolIcon = (type: string) => {
    switch (type) {
      case 'linter': return <Shield className="h-4 w-4" />;
      case 'formatter': return <Zap className="h-4 w-4" />;
      case 'bundler': return <Layers className="h-4 w-4" />;
      case 'package-manager': return <Package className="h-4 w-4" />;
      case 'version-control': return <GitBranch className="h-4 w-4" />;
      default: return <Wrench className="h-4 w-4" />;
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-500">
              <p>Failed to analyze project: {error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-muted-foreground">
              <p>No analysis data available</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Project Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              {projectName} - Code Analysis
            </div>
            <button
              onClick={loadProjectAnalysis}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1 text-sm border rounded hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{formatNumber(metrics.totalFiles)}</div>
              <div className="text-sm text-muted-foreground">Files</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{formatNumber(metrics.totalLines)}</div>
              <div className="text-sm text-muted-foreground">Lines</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{formatNumber(metrics.totalCharacters)}</div>
              <div className="text-sm text-muted-foreground">Characters</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{formatBytes(metrics.totalSize)}</div>
              <div className="text-sm text-muted-foreground">Size</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Programming Languages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Programming Languages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {metrics.languages.map((lang) => (
              <div key={lang.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: lang.color }}
                    />
                    <span className="font-medium">{lang.name}</span>
                    <Badge variant="secondary">{lang.files} files</Badge>
                  </div>
                  <span className="text-sm text-muted-foreground">{lang.percentage}%</span>
                </div>
                <Progress value={lang.percentage} className="h-2" />
                <div className="text-xs text-muted-foreground">
                  {formatNumber(lang.lines)} lines • {formatNumber(lang.characters)} characters
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Frameworks & Libraries */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Frameworks & Libraries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {metrics.frameworks.map((framework) => (
              <div key={framework.name} className="flex items-center gap-3 p-3 border rounded-lg">
                {getFrameworkIcon(framework.type)}
                <div className="flex-1">
                  <div className="font-medium">{framework.name}</div>
                  <div className="text-sm text-muted-foreground capitalize">{framework.type}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">{Math.round(framework.confidence * 100)}%</div>
                  <div className="text-xs text-muted-foreground">confidence</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Development Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Development Tools
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {metrics.tools.map((tool) => (
              <div key={tool.name} className="flex items-center gap-2 p-2 border rounded">
                {getToolIcon(tool.type)}
                <div>
                  <div className="text-sm font-medium">{tool.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{tool.type}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dependencies */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Dependencies
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {metrics.dependencies.map((dep) => (
              <div key={dep.name} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{dep.name}</div>
                  <div className="text-sm text-muted-foreground">{dep.description}</div>
                </div>
                <div className="text-right">
                  <Badge variant={dep.type === 'dependency' ? 'default' : 'secondary'}>
                    {dep.version}
                  </Badge>
                  <div className="text-xs text-muted-foreground mt-1 capitalize">
                    {dep.type.replace('Dependency', '')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Project Structure */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Project Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium mb-2">Directories</h4>
              <div className="space-y-1">
                {metrics.structure.directories.map((dir) => (
                  <div key={dir} className="text-sm text-muted-foreground">📁 {dir}</div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Configuration Files</h4>
              <div className="space-y-1">
                {metrics.structure.configFiles.map((file) => (
                  <div key={file} className="text-sm text-muted-foreground">⚙️ {file}</div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Documentation</h4>
              <div className="space-y-1">
                {metrics.structure.documentationFiles.map((file) => (
                  <div key={file} className="text-sm text-muted-foreground">📄 {file}</div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Tests</h4>
              <div className="space-y-1">
                {metrics.structure.testFiles.map((file) => (
                  <div key={file} className="text-sm text-muted-foreground">🧪 {file}</div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Complexity Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Complexity Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {metrics.complexity.averageLinesPerFile.toFixed(1)}
              </div>
              <div className="text-sm text-muted-foreground">Avg Lines/File</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {metrics.complexity.largestFile.lines}
              </div>
              <div className="text-sm text-muted-foreground">Largest File</div>
              <div className="text-xs text-muted-foreground mt-1">
                {metrics.complexity.largestFile.name}
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {metrics.complexity.mostComplexLanguage}
              </div>
              <div className="text-sm text-muted-foreground">Most Complex</div>
            </div>
          </div>
          
          {metrics.complexity.testCoverage && (
            <div className="mt-4 p-4 border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Test Coverage</span>
                <span className="text-sm text-muted-foreground">
                  {metrics.complexity.testCoverage}%
                </span>
              </div>
              <Progress value={metrics.complexity.testCoverage} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors">
              <Activity className="h-4 w-4" />
              <span className="text-sm">Run Tests</span>
            </button>
            <button className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors">
              <Shield className="h-4 w-4" />
              <span className="text-sm">Lint Code</span>
            </button>
            <button className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors">
              <Download className="h-4 w-4" />
              <span className="text-sm">Build Project</span>
            </button>
            <button className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors">
              <Globe className="h-4 w-4" />
              <span className="text-sm">Deploy</span>
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
